// Mock-данные AML-сценариев на основе AML.txt
// Используется только в MVP (фронтенд без бэкенда)

export type ScenarioCategory =
  | 'aml_check'
  | 'velocity'
  | 'pattern'
  | 'limits'
  | 'geo'
  | 'dormant'
  | 'accumulation'
  | 'first_transaction'
  | 'cumulative'
  | 'keyword'
  | 'blacklist'
  | 'geo_risk'
  | 'turnover_anomaly'
  | 'scoring'
  | 'user_management';

export interface ScenarioParam {
  key: string;
  label: string;
  value: number | string;
  unit?: string;
  description?: string;
}

export interface AmlScenario {
  id: string;
  code: string;
  name: string;
  description: string;
  category: ScenarioCategory;
  riskCoefficient: number; // 0–100
  params: ScenarioParam[];
  triggerAction: 'notify' | 'block' | 'review' | 'block_and_notify';
  isActive: boolean;
  regulatoryBasis: string;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionScenarioTrigger {
  scenarioId: string;
  scenarioCode: string;
  scenarioName: string;
  triggeredAt: string;
  riskContribution: number; // вклад в итоговый риск-скор (0–100)
  criteriaDescription: string; // описание конкретного условия срабатывания
  params: Record<string, string | number>;
}

export const CATEGORY_LABELS: Record<ScenarioCategory, string> = {
  aml_check: 'AML-проверка',
  velocity: 'Частота операций',
  pattern: 'Паттерн',
  limits: 'Лимиты',
  geo: 'География',
  dormant: 'Бездействие',
  accumulation: 'Накопление',
  first_transaction: 'Первая операция',
  cumulative: 'Совокупный объём',
  keyword: 'Ключевые слова',
  blacklist: 'Чёрный список',
  geo_risk: 'Высокорисковые страны',
  turnover_anomaly: 'Аномалия оборота',
  scoring: 'Скоринг',
  user_management: 'Управление пользователем',
};

// ─── Справочник сценариев ─────────────────────────────────────────────────────

export const AML_SCENARIOS: AmlScenario[] = [
  {
    id: 'scn-001',
    code: 'SCN-001',
    name: 'Первичная AML-проверка (SumSub)',
    description:
      'Автоматическая AML-проверка при регистрации или крупной транзакции. Система проверяет пользователя по санкционным спискам (EU Consolidated Sanctions List, UN Sanctions, FATF), базе PEP и спискам лиц, связанных с терроризмом. Транзакционная активность анализируется на наличие подозрительных паттернов.',
    category: 'aml_check',
    riskCoefficient: 75,
    params: [
      { key: 'threshold_amount', label: 'Порог суммы транзакции', value: 10000, unit: 'EUR', description: 'Сумма, при превышении которой инициируется AML-проверка (AMLD6 обязательный порог)' },
      { key: 'check_sanctions', label: 'Проверка санкционных списков', value: 'enabled' },
      { key: 'check_pep', label: 'Проверка PEP', value: 'enabled' },
      { key: 'check_terrorism', label: 'Проверка списков терроризма', value: 'enabled' },
    ],
    triggerAction: 'review',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), AMLR (EU 2024/1624), FATF Recommendations, EU Consolidated Sanctions List',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-002',
    code: 'SCN-002',
    name: 'Полная и частичная блокировка пользователя',
    description:
      'Сценарий блокировки клиента оператором. Полная блокировка: клиент не может авторизоваться в ЛК. Частичная блокировка: клиент видит операции и средства, но не может совершать операции вывода, оплаты, пополнения. Система отменяет активные операции и уведомляет клиента.',
    category: 'user_management',
    riskCoefficient: 90,
    params: [
      { key: 'block_type', label: 'Тип блокировки', value: 'full_or_partial', description: 'full — полная, partial — частичная' },
      { key: 'cancel_active_ops', label: 'Отмена активных операций', value: 'enabled' },
      { key: 'notify_client', label: 'Уведомление клиента', value: 'enabled' },
    ],
    triggerAction: 'block_and_notify',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), внутренние политики компании',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-003',
    code: 'SCN-003',
    name: 'Превышение количества транзакций за период (velocity)',
    description:
      'Выявление подозрительной активности, когда клиент отправляет или получает X или более транзакций за X минут. Может указывать на мошенничество или отмывание денег (smurfing, layering).',
    category: 'velocity',
    riskCoefficient: 70,
    params: [
      { key: 'max_transactions', label: 'Макс. количество транзакций', value: 10, unit: 'шт', description: 'Порог срабатывания по количеству транзакций' },
      { key: 'time_window_minutes', label: 'Временное окно', value: 10, unit: 'мин', description: 'Период наблюдения' },
      { key: 'direction', label: 'Направление', value: 'both', description: 'both — входящие и исходящие, in — только входящие, out — только исходящие' },
    ],
    triggerAction: 'notify',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), FATF R.20, внутренние политики',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-004',
    code: 'SCN-004',
    name: 'Круговая транзакция (получение и возврат суммы в тот же день)',
    description:
      'Выявление подозрительных транзакций, когда клиент получает или отправляет средства и в тот же календарный день совершает обратную транзакцию на сумму X1%–X2% от первоначальной. Может указывать на попытки отмывания денег (round-tripping).',
    category: 'pattern',
    riskCoefficient: 80,
    params: [
      { key: 'percent_min', label: 'Минимальный процент возврата', value: 90, unit: '%', description: 'Нижняя граница диапазона обратной транзакции' },
      { key: 'percent_max', label: 'Максимальный процент возврата', value: 110, unit: '%', description: 'Верхняя граница диапазона обратной транзакции' },
      { key: 'same_day', label: 'Период', value: 'same_calendar_day', description: 'Транзакции должны быть в пределах одного календарного дня (00:00–23:59)' },
    ],
    triggerAction: 'review',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), FATF R.10',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-005',
    code: 'SCN-005',
    name: 'Нарушение лимитов транзакций',
    description:
      'Контроль соответствия транзакций установленным лимитам. Порядок проверки: скоринг → фрод-лимиты → базовые лимиты → ограничения на пользователе → пользовательские лимиты. При превышении система отклоняет операцию с уведомлением.',
    category: 'limits',
    riskCoefficient: 60,
    params: [
      { key: 'check_scoring', label: 'Скоринг', value: 'enabled' },
      { key: 'check_fraud_limits', label: 'Фрод-лимиты', value: 'enabled' },
      { key: 'check_base_limits', label: 'Базовые лимиты', value: 'enabled' },
      { key: 'check_user_limits', label: 'Пользовательские лимиты', value: 'enabled' },
    ],
    triggerAction: 'block',
    isActive: true,
    regulatoryBasis: 'AMLR (EU 2024/1624), TFR (EU 2015/847), внутренние лимитные политики',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-006',
    code: 'SCN-006',
    name: 'Операции с X и более странами за период',
    description:
      'Контроль транзакций клиента, направленных в или полученных из X разных стран за последние X дней. Помогает выявлять подозрительные международные операции, обход санкций и отмывание денег через несколько юрисдикций.',
    category: 'geo',
    riskCoefficient: 75,
    params: [
      { key: 'max_countries', label: 'Макс. количество стран', value: 5, unit: 'стран', description: 'Порог по количеству уникальных стран' },
      { key: 'period_days', label: 'Период наблюдения', value: 30, unit: 'дней' },
      { key: 'direction', label: 'Направление', value: 'both' },
    ],
    triggerAction: 'review',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), FATF R.10/13, Базельский комитет',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-007',
    code: 'SCN-007',
    name: 'Транзакция после периода бездействия',
    description:
      'Определение первой транзакции после длительного периода бездействия клиента (X дней). Особое внимание уделяется, если первая транзакция после неактивности превышает установленный лимит суммы.',
    category: 'dormant',
    riskCoefficient: 55,
    params: [
      { key: 'inactivity_days', label: 'Период бездействия', value: 90, unit: 'дней', description: 'Минимальное количество дней без транзакций' },
      { key: 'amount_threshold', label: 'Пороговая сумма первой операции', value: 1000, unit: 'EUR' },
      { key: 'alert_once', label: 'Уведомление только для первой транзакции', value: 'enabled' },
    ],
    triggerAction: 'notify',
    isActive: true,
    regulatoryBasis: 'AMLD5 (EU 2018/843), FATF R.10, внутренние политики',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-008',
    code: 'SCN-008',
    name: 'Многократные транзакции одному получателю с превышением лимита',
    description:
      'Контроль многократных транзакций от одного отправителя к одному получателю, если общая сумма этих транзакций превышает установленный лимит X за определённый период. Помогает выявлять дробление (smurfing) и накопительные схемы.',
    category: 'accumulation',
    riskCoefficient: 70,
    params: [
      { key: 'max_transactions', label: 'Макс. количество транзакций', value: 5, unit: 'шт' },
      { key: 'total_amount_limit', label: 'Лимит совокупной суммы', value: 3000, unit: 'EUR' },
      { key: 'period_days', label: 'Период наблюдения', value: 1, unit: 'дней' },
      { key: 'history_days', label: 'Глубина истории проверки', value: 30, unit: 'дней' },
    ],
    triggerAction: 'review',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), FATF R.10',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-010',
    code: 'SCN-010',
    name: 'Первая транзакция с момента открытия счёта (высокорисковая категория)',
    description:
      'Обработка первой входящей или исходящей транзакции клиента с момента открытия счёта, если его категория входит в список, требующий дополнительного контроля (PEP, иностранные граждане, высокорисковые профессии и др.).',
    category: 'first_transaction',
    riskCoefficient: 65,
    params: [
      { key: 'high_risk_categories', label: 'Высокорисковые категории', value: 'PEP, foreign_nationals, high_risk_business', description: 'Список категорий через запятую' },
      { key: 'pause_transaction', label: 'Приостановить транзакцию', value: 'enabled' },
    ],
    triggerAction: 'review',
    isActive: true,
    regulatoryBasis: 'AMLD5 (EU 2018/843), FATF R.12',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-011',
    code: 'SCN-011',
    name: 'Превышение совокупной суммы транзакций по категории клиента',
    description:
      'Отслеживание транзакций пользователей определённой категории, у которых общая сумма входящих или исходящих транзакций превышает установленный порог X в течение последних X дней. Суммы конвертируются в базовую валюту для корректного сравнения.',
    category: 'cumulative',
    riskCoefficient: 75,
    params: [
      { key: 'cumulative_threshold', label: 'Совокупный порог суммы', value: 10000, unit: 'EUR' },
      { key: 'period_days', label: 'Период наблюдения', value: 30, unit: 'дней' },
      { key: 'base_currency', label: 'Базовая валюта', value: 'EUR' },
      { key: 'direction', label: 'Направление', value: 'both' },
    ],
    triggerAction: 'notify',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), FATF R.10',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-012',
    code: 'SCN-012',
    name: 'Подозрительные ключевые слова в описании транзакции',
    description:
      'Обнаружение транзакций, в описании которых содержатся ключевые слова или фразы, связанные с подозрительными операциями (например: "подарок", "долг", "неизвестный платёж", "наличные"), при условии превышения пороговой суммы X.',
    category: 'keyword',
    riskCoefficient: 70,
    params: [
      { key: 'keywords', label: 'Ключевые слова', value: 'подарок,долг,неизвестный,наличные,криптовалюта,дар', description: 'Список ключевых слов через запятую' },
      { key: 'amount_threshold', label: 'Пороговая сумма', value: 500, unit: 'EUR' },
      { key: 'case_sensitive', label: 'Учёт регистра', value: 'disabled' },
    ],
    triggerAction: 'review',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), FATF R.20, внутренние политики комплаенса',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-013',
    code: 'SCN-013',
    name: 'Транзакция с контрагентом из внутреннего чёрного списка',
    description:
      'Автоматическое выявление и блокировка транзакций, в которых отправитель или получатель находится во внутреннем чёрном списке платформы. Помогает предотвратить взаимодействие с лицами, ассоциированными с мошенничеством, отмыванием денег или финансированием терроризма.',
    category: 'blacklist',
    riskCoefficient: 95,
    params: [
      { key: 'blacklist_type', label: 'Тип чёрного списка', value: 'internal', description: 'internal — внутренний список платформы' },
      { key: 'check_sender', label: 'Проверять отправителя', value: 'enabled' },
      { key: 'check_receiver', label: 'Проверять получателя', value: 'enabled' },
      { key: 'auto_block', label: 'Автоматическая блокировка', value: 'enabled' },
    ],
    triggerAction: 'block_and_notify',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), EU Consolidated Sanctions List, внутренние политики безопасности',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-015',
    code: 'SCN-015',
    name: 'Транзакции с высокорисковыми странами (FATF)',
    description:
      'Контроль транзакций, при которых клиент отправляет или получает сумму, превышающую пороговое значение X, в/из страны из списка "Страны высокого риска" (FATF blacklist/greylist). Анализируется совокупный объём транзакций за период.',
    category: 'geo_risk',
    riskCoefficient: 80,
    params: [
      { key: 'amount_threshold', label: 'Пороговая сумма транзакции', value: 5000, unit: 'EUR' },
      { key: 'period_days', label: 'Период анализа', value: 30, unit: 'дней' },
      { key: 'cumulative_threshold', label: 'Совокупный порог за период', value: 15000, unit: 'EUR' },
      { key: 'list_source', label: 'Источник списка стран', value: 'FATF_blacklist_greylist' },
    ],
    triggerAction: 'block_and_notify',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), FATF R.19, EU Consolidated Sanctions List',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-016',
    code: 'SCN-016',
    name: 'Аномальный рост оборота относительно среднемесячного',
    description:
      'Отслеживание резкого увеличения объёма входящих или исходящих транзакций клиента, которое превышает X% от установленного среднемесячного кредитного оборота. Среднемесячный оборот рассчитывается за предыдущие 3–6 месяцев.',
    category: 'turnover_anomaly',
    riskCoefficient: 70,
    params: [
      { key: 'growth_threshold_percent', label: 'Порог роста оборота', value: 300, unit: '%', description: 'Превышение среднемесячного оборота на X%' },
      { key: 'baseline_months', label: 'Период расчёта базового оборота', value: 3, unit: 'мес' },
      { key: 'direction', label: 'Направление', value: 'both' },
    ],
    triggerAction: 'notify',
    isActive: true,
    regulatoryBasis: 'AMLD6 (EU 2018/1673), FATF R.20, внутренние политики комплаенса',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
  {
    id: 'scn-018',
    code: 'SCN-018',
    name: 'Скоринг транзакций (автоматическая оценка риска)',
    description:
      'Автоматическое присвоение балла риска каждой транзакции на основе комплексного анализа факторов: профиль пользователя, история операций, паттерны активности, геоданные, KYC-статус. При превышении балла порога — инициируется дополнительная проверка.',
    category: 'scoring',
    riskCoefficient: 85,
    params: [
      { key: 'score_threshold', label: 'Порог скор-балла', value: 70, unit: 'баллов', description: 'При превышении — инициируется ручная проверка' },
      { key: 'auto_block_score', label: 'Балл автоматической блокировки', value: 90, unit: 'баллов' },
      { key: 'model_version', label: 'Версия модели', value: 'v2.1' },
    ],
    triggerAction: 'review',
    isActive: true,
    regulatoryBasis: 'AMLR (EU 2024/1624), FATF R.10/20, внутренние политики управления рисками',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-01T08:00:00Z',
  },
];

// ─── Mock-триггеры сценариев для транзакций ───────────────────────────────────

export const MOCK_TRANSACTION_TRIGGERS: Record<string, TransactionScenarioTrigger[]> = {
  // Пример: трnзакция с ID xxx попала под несколько сценариев
  'TRX-MOCK-001': [
    {
      scenarioId: 'scn-003',
      scenarioCode: 'SCN-003',
      scenarioName: 'Превышение количества транзакций за период (velocity)',
      triggeredAt: '2024-03-11T14:32:00Z',
      riskContribution: 70,
      criteriaDescription: 'Клиент совершил 12 транзакций за 10 минут (порог: 10 за 10 мин)',
      params: { actual_count: 12, window_minutes: 10, threshold: 10 },
    },
  ],
  'TRX-MOCK-002': [
    {
      scenarioId: 'scn-013',
      scenarioCode: 'SCN-013',
      scenarioName: 'Транзакция с контрагентом из внутреннего чёрного списка',
      triggeredAt: '2024-03-11T15:10:00Z',
      riskContribution: 95,
      criteriaDescription: 'Получатель acc-7f2a3b найден во внутреннем чёрном списке',
      params: { blacklisted_account: 'acc-7f2a3b', reason: 'fraud_confirmed' },
    },
    {
      scenarioId: 'scn-015',
      scenarioCode: 'SCN-015',
      scenarioName: 'Транзакции с высокорисковыми странами (FATF)',
      triggeredAt: '2024-03-11T15:10:00Z',
      riskContribution: 80,
      criteriaDescription: 'Страна получателя (IR) включена в список высокорисковых стран FATF',
      params: { country: 'IR', amount: 250000, threshold: 100000 },
    },
  ],
  'TRX-MOCK-003': [
    {
      scenarioId: 'scn-004',
      scenarioCode: 'SCN-004',
      scenarioName: 'Круговая транзакция (получение и возврат суммы в тот же день)',
      triggeredAt: '2024-03-11T16:45:00Z',
      riskContribution: 80,
      criteriaDescription: 'Получил 500 000 RUB в 09:15, отправил 493 000 RUB (98.6%) в 16:44 того же дня',
      params: { original_amount: 500000, return_amount: 493000, return_percent: 98.6, same_day: 1 },
    },
  ],
};

// Вспомогательные функции

export const findScenario = (code: string): AmlScenario | undefined =>
  AML_SCENARIOS.find((s) => s.code === code);

export const getRiskColor = (coefficient: number): string => {
  if (coefficient >= 80) return 'danger';
  if (coefficient >= 60) return 'warning';
  if (coefficient >= 40) return 'info';
  return 'secondary';
};

export const getTriggerActionLabel = (action: AmlScenario['triggerAction']): string => {
  const labels: Record<AmlScenario['triggerAction'], string> = {
    notify: 'Уведомление',
    block: 'Блокировка',
    review: 'Ручная проверка',
    block_and_notify: 'Блокировка + уведомление',
  };
  return labels[action];
};
